import React, { FC } from "react";

import { BaseComponentProps, KButtonAction } from "@keepeek/refront-components";
import { Box, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import EditableSanitizeHtml from "../../../containers/FrontEdit/EditableSanitizeHtml";
import { KIcon } from "../../common/KIcon";

const PREFIX = "MediaDetailHeaderNavigation";

const classes = {
  root: `${PREFIX}-root`,
  button: `${PREFIX}-button`,
  backLink: `${PREFIX}-backLink`,
};

const StyledMediaDetailHeaderNavigation = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    height: "100%",
    alignItems: "center",
  },

  [`& .${classes.backLink}`]: {
    "&:hover": {
      textDecoration: "none",
    },
  },
}));

export type MediaDetailHeaderNavigationProps = {
  hasPrevious?: boolean;
  hasNext?: boolean;
  onPreviousClick?: () => void;
  onNextClick?: () => void;
  onBackClick?: () => void;
  returnLabel: string;
} & BaseComponentProps;

const MediaDetailHeaderNavigation: FC<React.PropsWithChildren<MediaDetailHeaderNavigationProps>> =
  function MetadataInfo({
    className,
    hasPrevious,
    hasNext,
    onPreviousClick,
    onNextClick,
    onBackClick,
    returnLabel,
  }) {
    const { t } = useTranslation();
    return (
      <StyledMediaDetailHeaderNavigation
        className={clsx("MediaDetailHeaderNavigation", className, classes.root)}
      >
        <Box>
          <Button
            component="a"
            size="medium"
            className={classes.button}
            variant="text"
            onClick={onBackClick}
            aria-label={t(returnLabel) as string}
            color="default"
          >
            <KIcon
              fontSize="small"
              className={"fa-regular fa-arrow-left fa-lg"}
              sx={{ paddingInline: "3px" }}
            />
            &nbsp;
            <EditableSanitizeHtml labelKey={returnLabel} />
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: (theme) => theme.spacing(3),
          }}
        >
          <KButtonAction
            data-testid="previousElementNavigation"
            className={classes.button}
            onClick={onPreviousClick}
            disabled={!hasPrevious}
            component="a"
            color="default"
          >
            <KIcon className="fa-regular fa-chevron-left fa-sm" />
            <span className="sr-only">{t("media-detail.navigation.previous.label")}</span>
          </KButtonAction>

          <KButtonAction
            data-testid="nextElementNavigation"
            className={classes.button}
            onClick={onNextClick}
            disabled={!hasNext}
            color="default"
            component={"a"}
          >
            <KIcon className="fa-regular fa-chevron-right fa-sm" />
            <span className="sr-only">{t("media-detail.navigation.next.label")}</span>
          </KButtonAction>
        </Box>
      </StyledMediaDetailHeaderNavigation>
    );
  };

export default MediaDetailHeaderNavigation;
